import Badge from '@/components/Badge/Badge';
import Modal from '@/components/Modal/Modal';
import WidgetDetailChrome from '@/features/dashboard/components/WidgetDetailChrome';
import { computeSensorHealth, formatCompactAge, isLowBattery } from '@/features/homeAutomation/lib/freshness';
import type { Sensor } from '@/features/homeAutomation/types/sensor';

interface SensorsDetailModalProps {
  open: boolean;
  onClose: () => void;
  sensors: Sensor[];
  lastUpdated?: Date | null;
  onRefresh?: () => void;
}

function fmtTemp(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${v.toFixed(1)} °C`;
}

function fmtHumidity(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${Math.round(v)} %`;
}

function fmtPressure(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${Math.round(v)} hPa`;
}

function fmtBattery(mv: number | null | undefined): string {
  if (mv === null || mv === undefined || !Number.isFinite(mv)) return '—';
  return `${(mv / 1000).toFixed(2)} V`;
}

function fmtRssi(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${v} dBm`;
}

export default function SensorsDetailModal({ open, onClose, sensors, lastUpdated, onRefresh }: SensorsDetailModalProps) {
  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title="Sensors" description="Latest readings" size="lg">
      <WidgetDetailChrome widgetId="sensors" lastUpdated={lastUpdated} onRefresh={onRefresh} />

      {sensors.length === 0 ? (
        <p className="text-sm text-(--text-tertiary)">No sensors yet.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-(--border-subtle)">
          {sensors.map((sensor) => {
            const state = sensor.currentState;
            const health = computeSensorHealth(state);
            const showAge = (health.tier === 'aging' || health.tier === 'stale') && health.ageMs != null;
            return (
              <li key={sensor.id} className="flex flex-col gap-1.5 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-(--text-primary)">{sensor.name}</span>
                  {sensor.location && <span className="text-xs text-(--text-tertiary)">{sensor.location}</span>}
                  {showAge && <Badge tone={health.tier === 'stale' ? 'danger' : 'warning'}>{formatCompactAge(health.ageMs!)}</Badge>}
                  {isLowBattery(state?.batteryMv) && <Badge tone="warning">low batt</Badge>}
                </div>
                <dl className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
                  <Stat label="Temp" value={fmtTemp(state?.temperatureC)} />
                  <Stat label="Humidity" value={fmtHumidity(state?.humidityPct)} />
                  <Stat label="Pressure" value={fmtPressure(state?.pressureHpa)} />
                  <Stat label="Battery" value={fmtBattery(state?.batteryMv)} />
                  <Stat label="Signal" value={fmtRssi(state?.rssi)} />
                </dl>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-1">
      <dt className="text-xs text-(--text-muted)">{label}</dt>
      <dd className="text-(--text-primary) tabular-nums">{value}</dd>
    </div>
  );
}
